import React from 'react';
import { useLocation } from 'react-router-dom';
import ProductCard from '@99designs/apps/spa/common/Product/ProductCard';
import SecondaryProductCard from '@99designs/apps/spa/common/Product/ProductCard/SecondaryProductCard';
import { ProductFixedPriceProjectFragment } from '@99designs/apps/spa/pages/create/briefs/Brief.generated';
import { useProductsQuery } from '@99designs/apps/spa/pages/create/common/Products.generated';
import { ButtonWrapper } from '@99designs/apps/spa/pages/create/components/Button';
import ContentSetting from '@99designs/apps/spa/pages/create/components/ContentSetting';
import { parseQueryString } from '@99designs/apps/spa/pages/create/util/queryString';
import { PartnerLogo } from '@99designs/brief';
import { Box, Button, PageHeading, SmallPageHeading, Stack } from '@99designs/emu';
import { LoadableElement } from '@99designs/frontend-components/components/LoadableElement';
import { linkTo } from '@99designs/routing';
import { Partner } from '@99designs/spa-common';
import { ViewProductDetails } from '@99designs/spa-common';
import { packageToProductSummary } from '@99designs/spa-common';

function FacebookCreate() {
    const location = useLocation();
    const { designerId } = parseQueryString(location.search);

    const { error, data, loading } = useProductsQuery({
        variables: {
            input: { categories: ['facebook-ad'], types: ['PRODUCT_TYPE_FIXED_PRICE_PROJECT'] },
        },
    });

    if (error) {
        throw error;
    }

    if (loading) {
        return <LoadableElement variations={['fade-in']} isLoading={true} />;
    }

    if (!data) {
        throw new Error('package not found');
    }

    const facebookPackages = data.products as ProductFixedPriceProjectFragment[];

    return (
        <ContentSetting>
            <Box marginBottom="1.5bu">
                <PartnerLogo partner={Partner.Facebook} />
            </Box>
            <Box marginBottom="1.5bu">
                <PageHeading>{__('Select the option that best suits your need')}</PageHeading>
            </Box>
            <Stack space="8bu">
                {facebookPackages.map((facebookPackage, index) => {
                    const productSummary = packageToProductSummary({
                        title: facebookPackage.title,
                        description: facebookPackage.description,
                        amountInCents: facebookPackage.pricing.amount.amountInCents,
                        currency: facebookPackage.pricing.amount.currency,
                    });

                    return (
                        <Stack space="1.5bu" key={index}>
                            <ProductCard
                                rightColumn={
                                    <Stack space="1bu">
                                        <Box display="flex" justifyContent="center">
                                            <ViewProductDetails
                                                features={facebookPackage.details}
                                                additionalDetails={
                                                    facebookPackage.additionalDetails
                                                }
                                                productSummary={productSummary}
                                            />
                                        </Box>
                                        <ButtonWrapper>
                                            <Button
                                                size="large"
                                                appearance="primary"
                                                width="fill"
                                                href={linkTo.briefCreate(facebookPackage.key)}
                                                type="button"
                                            >
                                                {__('Start brief')}
                                            </Button>
                                        </ButtonWrapper>
                                    </Stack>
                                }
                                productSummary={productSummary}
                                features={facebookPackage.details}
                                hasLeftBorder={false}
                            />
                        </Stack>
                    );
                })}
                <Stack space="1.5bu">
                    <Stack space="0.5bu">
                        <SmallPageHeading>
                            {__('Got a different format or placement in mind?')}
                        </SmallPageHeading>
                        <div>
                            {__(
                                'Simply request a quote for the placements and formats you need for your Facebook creative campaign. Ask a professional designer for exactly what you’re after, and they’ll bring your story to life in no time.'
                            )}
                        </div>
                    </Stack>
                    <SecondaryProductCard
                        title={__('Custom Facebook creative project')}
                        description={__(
                            'Get tailored quotes from up to 5 designers based on your needs. Through a custom project, you can: create image, video or carousel ads; choose from a number of different placements including Facebook Feed, Facebook Stories, Facebook In-stream, Instagram Feed, Instagram Stories, and Messenger Inbox; brief your designer on the creative you specifically require for your Facebook campaign.'
                        )}
                        cta={
                            <ButtonWrapper>
                                <Button
                                    size="large"
                                    appearance="secondary"
                                    width="fill"
                                    href={linkTo.briefCreate('facebook-ad-custom', designerId)}
                                    type="button"
                                >
                                    {__('Get quote')}
                                </Button>
                            </ButtonWrapper>
                        }
                    />
                </Stack>
            </Stack>
        </ContentSetting>
    );
}

export default FacebookCreate;
